import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Search, Filter, Calendar, MapPin, Clock, 
  CheckCircle, XCircle, Loader2, IndianRupee
} from 'lucide-react';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import { GlowingCard } from '../../components/3DEffects';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminBookings() {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [updatingId, setUpdatingId] = useState(null);

  useEffect(() => {
    fetchBookings();
  }, []);

  const fetchBookings = async () => {
    try {
      const res = await fetch(`${API}/admin/bookings`, { credentials: 'include' });
      if (res.ok) {
        setBookings(await res.json());
      }
    } catch (error) {
      console.error('Failed to fetch bookings:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (bookingId, newStatus) => {
    setUpdatingId(bookingId);
    try {
      const res = await fetch(`${API}/admin/bookings/${bookingId}/status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ status: newStatus })
      });

      if (res.ok) {
        setBookings(bookings.map(b => 
          b.booking_id === bookingId ? { ...b, status: newStatus } : b
        ));
        toast.success('Status updated');
      }
    } catch (error) {
      toast.error('Failed to update status');
    } finally {
      setUpdatingId(null);
    }
  };

  const filteredBookings = bookings.filter(booking => {
    const matchesSearch = 
      booking.user?.email?.toLowerCase().includes(search.toLowerCase()) ||
      booking.package?.name?.toLowerCase().includes(search.toLowerCase()) ||
      booking.address?.toLowerCase().includes(search.toLowerCase());
    const matchesStatus = statusFilter === 'all' || booking.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const getStatusColor = (status) => {
    const colors = {
      pending: 'text-yellow-400 bg-yellow-400/10 border-yellow-400/30',
      confirmed: 'text-blue-400 bg-blue-400/10 border-blue-400/30',
      completed: 'text-green-400 bg-green-400/10 border-green-400/30',
      cancelled: 'text-red-400 bg-red-400/10 border-red-400/30'
    };
    return colors[status] || 'text-gray-400 bg-gray-400/10';
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-12 h-12 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div data-testid="admin-bookings">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <h1 className="text-2xl lg:text-3xl font-bold text-white mb-2">Manage Bookings</h1>
        <p className="text-gray-400">View and manage service appointments</p>
      </motion.div>

      {/* Filters */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col md:flex-row gap-4 mb-6"
      >
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search bookings..."
            className="pl-10 bg-white/5 border-white/10 text-white"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[180px] bg-white/5 border-white/10 text-white">
            <Filter className="w-4 h-4 mr-2 text-gray-400" />
            <SelectValue placeholder="Filter status" />
          </SelectTrigger>
          <SelectContent className="bg-[#0D1224] border-white/10">
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="confirmed">Confirmed</SelectItem>
            <SelectItem value="completed">Completed</SelectItem>
            <SelectItem value="cancelled">Cancelled</SelectItem>
          </SelectContent>
        </Select>
      </motion.div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        {['pending', 'confirmed', 'completed', 'cancelled'].map((status) => {
          const count = bookings.filter(b => b.status === status).length;
          return (
            <motion.div
              key={status}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className={`p-4 rounded-xl text-center ${getStatusColor(status)} border`}
            >
              <div className="text-2xl font-bold">{count}</div>
              <div className="text-xs capitalize">{status}</div>
            </motion.div>
          );
        })}
      </div>

      {/* Bookings Grid */}
      {filteredBookings.length === 0 ? (
        <div className="text-center py-16">
          <Calendar className="w-12 h-12 text-gray-600 mx-auto mb-4" />
          <p className="text-gray-400">No bookings found</p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredBookings.map((booking, idx) => (
            <motion.div
              key={booking.booking_id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: idx * 0.03 }}
            >
              <GlowingCard className="p-5 rounded-xl h-full">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="text-white font-semibold">
                      {booking.package?.name || booking.package_id}
                    </h3>
                    <p className="text-gray-500 text-sm">{booking.user?.email}</p>
                  </div>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(booking.status)}`}>
                    {booking.status}
                  </span>
                </div>

                <div className="space-y-2 mb-4">
                  <div className="flex items-center gap-2 text-sm text-gray-400">
                    <Calendar className="w-4 h-4 text-blue-400" />
                    <span>{formatDate(booking.scheduled_date)}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-400">
                    <MapPin className="w-4 h-4 text-blue-400" />
                    <span className="truncate">{booking.address}</span>
                  </div>
                  {booking.package?.price && (
                    <div className="flex items-center gap-2 text-sm text-green-400">
                      <IndianRupee className="w-4 h-4" />
                      <span>₹{booking.package.price}</span>
                    </div>
                  )}
                </div>

                {booking.notes && (
                  <p className="text-gray-500 text-xs mb-4 p-2 bg-white/5 rounded">
                    {booking.notes}
                  </p>
                )}

                {/* Status Actions */}
                <div className="flex gap-2 pt-4 border-t border-white/10">
                  {booking.status === 'pending' && (
                    <>
                      <Button
                        size="sm"
                        onClick={() => updateStatus(booking.booking_id, 'confirmed')}
                        disabled={updatingId === booking.booking_id}
                        className="flex-1 bg-blue-500/20 text-blue-400 hover:bg-blue-500/30 border border-blue-500/30"
                      >
                        {updatingId === booking.booking_id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <>
                            <CheckCircle className="w-4 h-4 mr-1" />
                            Confirm
                          </>
                        )}
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => updateStatus(booking.booking_id, 'cancelled')}
                        disabled={updatingId === booking.booking_id}
                        className="bg-red-500/20 text-red-400 hover:bg-red-500/30 border border-red-500/30"
                      >
                        <XCircle className="w-4 h-4" />
                      </Button>
                    </>
                  )}
                  {booking.status === 'confirmed' && (
                    <Button
                      size="sm"
                      onClick={() => updateStatus(booking.booking_id, 'completed')}
                      disabled={updatingId === booking.booking_id}
                      className="flex-1 bg-green-500/20 text-green-400 hover:bg-green-500/30 border border-green-500/30"
                    >
                      {updatingId === booking.booking_id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <>
                          <CheckCircle className="w-4 h-4 mr-1" />
                          Complete
                        </>
                      )}
                    </Button>
                  )}
                </div>
              </GlowingCard>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
}
